import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  Option,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  paginationWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    '& button': {
      minWidth: 'fit-content',
      padding: '0px',
    },
  },
  unsizedIcon: {
    width: '24px',
    height: '24px',
  },
  pageDropdown: {
    border: '1px solid #B3B3B3',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: '70px',
    borderRadius: '2px',
    '& button': {
      padding: '5px 4px 5px 8px',
    },
  },
});

const Pagination = ({
  totalPage,
  currentPage,
  onPageChange,
}) => {
  const classes = useStyles();
  const handleFirst = useCallback(() => {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  }, [currentPage, onPageChange]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPage) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, onPageChange, totalPage]);

  const handleLast = useCallback(() => {
    if (currentPage !== totalPage) {
      onPageChange(totalPage);
    }
  }, [currentPage, onPageChange, totalPage]);

  const handlePageSelect = (ev, data) => {
    onPageChange(Number(data.optionValue));
  };

  const pageOptions = Array.from({ length: totalPage }, (_, i) => i + 1).map(page => (
    <Option key={page}>{page.toString()}</Option>
  ));

  return (
    <div className={classes.paginationWrapper}>
      <Button
        onClick={handleFirst}
        disabledFocusable={currentPage === 1}
        shape="square"
        appearance="transparent"
        aria-label="First page button"
      >
        <ChevronDoubleLeftRegular className={classes.unsizedIcon} />
      </Button>
      <Button
        onClick={handlePrev}
        disabledFocusable={currentPage === 1}
        shape="square"
        appearance="transparent"
        aria-label="Previous page button"
      >
        <ChevronLeft24Regular />
      </Button>
      <Dropdown
        className={classes.pageDropdown}
        value={currentPage.toString()}
        selectedOptions={[currentPage.toString()]}
        onOptionSelect={handlePageSelect}
        aria-label="Page dropdown"
      >
        {pageOptions}
      </Dropdown>
      <Text>{` of ${totalPage}`}</Text>
      <Button
        onClick={handleNext}
        disabledFocusable={currentPage === totalPage}
        shape="square"
        appearance="transparent"
        aria-label="Next page"
      >
        <ChevronRight24Regular />
      </Button>
      <Button
        onClick={handleLast}
        disabledFocusable={currentPage === totalPage}
        shape="square"
        appearance="transparent"
        aria-label="Last page"
      >
        <ChevronDoubleRightRegular className={classes.unsizedIcon} />
      </Button>
    </div>
  );
};

Pagination.propTypes = {
  totalPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export { Pagination };
